/**
 * Types for the Ideas domain
 */

export type IdeaContentType = 'POST' | 'REEL' | 'CAROUSEL' | 'STORY';
export type IdeaStatus = 'DRAFT' | 'PENDING' | 'APPROVED' | 'READY' | 'REJECTED' | 'COMPLETE';

export type IdeaBodySectionKey = 'objective' | 'caption' | 'voiceover_script' | 'storyboard' | 'concept';

// Concept leads — it is the idea's actual work; the rest are supporting
// sections (DD-013).
export const IDEA_BODY_SECTIONS: { key: IdeaBodySectionKey; label: string }[] = [
  { key: 'concept', label: 'Concept' },
  { key: 'objective', label: 'Objective' },
  { key: 'caption', label: 'Caption' },
  { key: 'voiceover_script', label: 'Voiceover Script' },
  { key: 'storyboard', label: 'Storyboard' },
];

export type IdeaBody = Partial<Record<IdeaBodySectionKey, string>>;

export type IdeaResourceEmbedStatus = 'ok' | 'og_fallback' | 'failed';

export type IdeaResourceEmbedProvider =
  | 'instagram'
  | 'pinterest'
  | 'tiktok'
  | 'youtube'
  | 'generic';

export type IdeaResourceEmbedType =
  | 'post'
  | 'reel'
  | 'carousel'
  | 'pin'
  | 'video'
  | 'article';

export type IdeaResourceLocalMediaStatus =
  | 'ok'
  | 'restricted'
  | 'blocked'
  | 'failed';

export interface IdeaResourceLocalMediaItem {
  gcsPath: string;
  url?: string | null;
  mediaType: string;
  width?: number | null;
  height?: number | null;
  durationSec?: number | null;
}

export interface IdeaResourceEmbed {
  provider: IdeaResourceEmbedProvider;
  embedType: IdeaResourceEmbedType;
  status: IdeaResourceEmbedStatus;
  title?: string | null;
  authorName?: string | null;
  thumbnailUrl?: string | null;
  html?: string | null;
  fetchedAt: string;
  ttlSeconds: number;
  localMedia?: IdeaResourceLocalMediaItem[];
  localMediaStatus?: IdeaResourceLocalMediaStatus;
  localMediaFetchedAt?: string;
  localMediaError?: string;
}

export interface IdeaResource {
  url: string;
  note?: string;
  embed?: IdeaResourceEmbed;
}

export interface IdeaNote {
  id: string;
  content: string;
  tags: string[];
  createdAt: string;
}

export interface IdeaPost {
  id: string;
  status: string;
  postType: string;
  isCarousel: boolean;
  mediaItems: { id: number; thumbnailUrl?: string }[];
}

export interface IdeaEntityLink {
  id: string;
  sourceType: string;
  sourceId: string;
  targetType: string;
  targetId: string;
  targetName?: string | null;
  year: number | null;
  createdAt: string | null;
  createdBy: string | null;
}

export interface Idea {
  id: string;
  clientId: string;
  briefId: string | null;
  title: string;
  contentType: IdeaContentType | null;
  /**
   * Idea objective type key (commercial / awareness / informative / ...),
   * chosen at creation. Distinct from contentType (POST/REEL/...) and
   * contentCategories. Resolve the key to a label via the objective-types
   * read hook; fall back to the raw key when unknown.
   */
  objectiveType?: string | null;
  resources: IdeaResource[];
  body: IdeaBody;
  platforms: string[];
  contentCategories: string[];
  status: IdeaStatus;
  clientFeedback: string | null;
  /** Client portal review: 1-5 stars, set on PATCH /ideas/:id/review. */
  clientRating?: number | null;
  /** Client portal review: optional free-text comment alongside the rating. */
  clientComment?: string | null;
  /** ISO timestamp of the client's most recent review write. */
  clientReviewedAt?: string | null;
  notes?: IdeaNote[];
  posts?: IdeaPost[];
  links?: IdeaEntityLink[];
  createdAt: string;
  updatedAt: string | null;
}

export interface CreateIdeaRequest {
  title: string;
  /** Convenience for plain-text callers; the backend lands it in body.concept (TD-030). */
  concept?: string;
  contentType?: IdeaContentType;
  objectiveType?: string | null;
  resources?: IdeaResource[];
  body?: IdeaBody;
  platforms?: string[];
  contentCategories?: string[];
}

export interface UpdateIdeaRequest {
  title?: string;
  concept?: string | null;
  contentType?: IdeaContentType;
  objectiveType?: string | null;
  resources?: IdeaResource[];
  status?: IdeaStatus;
  body?: IdeaBody;
  platforms?: string[];
  contentCategories?: string[];
}

export interface IdeasResponse {
  ideas: Idea[];
}

// Query keys factory
export const ideaKeys = {
  all: ['ideas'] as const,
  lists: () => [...ideaKeys.all, 'list'] as const,
  list: (clientId: string, status?: IdeaStatus) =>
    [...ideaKeys.lists(), clientId, { status }] as const,
  details: () => [...ideaKeys.all, 'detail'] as const,
  detail: (id: string) => [...ideaKeys.details(), id] as const,
  approved: (clientId: string) => [...ideaKeys.all, 'approved', clientId] as const,
};
